/**
 * `TRACE_animation_binding` — Babylon glTF loader hook: every node the loader
 * creates learns the binding names of the file it came from.
 *
 * Babylon already stamps each created node with the JSON pointers of the glTF
 * objects it came from (`_internalMetadata.gltf.pointers`, see
 * `utils/GltfPointers.ts`) — the file's own INDICES. Binding by name needs the
 * other half: which index each name keys in THAT file. So once per load, after
 * every node exists (`onReady`), this computes `resolveFileNames(json)` and
 * hangs the one shared result beside the pointers. It survives container
 * instancing the same way the pointers do (`instantiateWithGltfPointers`), so
 * every load path — the scene plugin's inline import, the `Model` behavior's
 * container, a direct import — answers "what are this instance's names?" the
 * same way.
 *
 * Registered for EVERY file, not only files that declare the extension: plain
 * glTF 2.0 files bind by their node and morph target names too (a performance
 * file and most avatars never mention the extension).
 *
 * It also stamps Trace's driven animations (`animations[k].extras.trace.driver`,
 * `utils/drivenAnimation.ts`), which are not part of the extension.
 */

import type { Node } from "@babylonjs/core/node";
import type { GLTFLoader } from "@babylonjs/loaders/glTF/2.0/glTFLoader";
import type { IGLTFLoaderExtension } from "@babylonjs/loaders/glTF/2.0/glTFLoaderExtension";
import { registerGLTFExtension, unregisterGLTFExtension } from "@babylonjs/loaders/glTF/2.0/glTFLoaderExtensionRegistry";
import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import { TRACE_ANIMATION_BINDING, resolveFileNames, type FileNames } from "../formats/gltf21/animationBinding";
import { setAnimationDriver } from "../../utils/drivenAnimation";

interface StampedMetadata {
    gltf?: { pointers?: string[]; fileNames?: FileNames; primitiveOfNode?: number };
}

type WithInternalMetadata = { _internalMetadata?: StampedMetadata };

/** The binding names of the file a loaded node came from, or undefined for a node no glTF load created. */
export function getGltfFileNames(node: Node): FileNames | undefined {
    return (node as unknown as WithInternalMetadata)._internalMetadata?.gltf?.fileNames;
}

/**
 * The glTF node index whose mesh a loaded primitive mesh belongs to — the
 * identity that survives Babylon's skinning layout, where a skinned node's
 * primitives are re-parented beside the skeleton root, away from the
 * transform node that carries `/nodes/{i}`.
 */
export function getGltfPrimitiveNode(node: Node): number | undefined {
    return (node as unknown as WithInternalMetadata)._internalMetadata?.gltf?.primitiveOfNode;
}

function stamp(object: unknown, names: FileNames, primitiveOfNode?: number): void {
    if (!object) return;
    const target = object as WithInternalMetadata;
    const metadata = (target._internalMetadata = target._internalMetadata ?? {});
    const gltf = (metadata.gltf = metadata.gltf ?? {});
    gltf.fileNames = names;
    if (primitiveOfNode !== undefined) gltf.primitiveOfNode = primitiveOfNode;
}

interface LoadedGltfAnimation {
    _babylonAnimationGroup?: AnimationGroup;
    extras?: { trace?: { driver?: { node?: unknown; target?: unknown } } };
    samplers?: Array<{ input?: number }>;
}

/**
 * Stamp every driven animation's group with its driver and its clip's input
 * range (the accessors' required `min`/`max`), so the runtime keeps it out of
 * the clip list and evaluates it from the driver instead.
 */
function stampDrivers(gltf: { animations?: unknown[]; accessors?: unknown[] }): void {
    (gltf.animations as LoadedGltfAnimation[] | undefined)?.forEach((animation, index) => {
        const driver = animation.extras?.trace?.driver;
        const group = animation._babylonAnimationGroup;
        if (!driver || typeof driver.node !== "string" || typeof driver.target !== "string" || !group) return;
        let inputMin = Infinity;
        let inputMax = -Infinity;
        for (const sampler of animation.samplers ?? []) {
            const accessor = (gltf.accessors as Array<{ min?: number[]; max?: number[] }> | undefined)?.[sampler.input ?? -1];
            if (typeof accessor?.min?.[0] === "number") inputMin = Math.min(inputMin, accessor.min[0]);
            if (typeof accessor?.max?.[0] === "number") inputMax = Math.max(inputMax, accessor.max[0]);
        }
        if (!Number.isFinite(inputMin)) inputMin = 0;
        if (!Number.isFinite(inputMax)) {
            const fps = group.targetedAnimations[0]?.animation.framePerSecond ?? 60;
            inputMax = group.to / fps;
        }
        setAnimationDriver(group, { node: driver.node, target: driver.target, index, inputMin, inputMax });
    });
}

interface LoadedGltfNode {
    _babylonTransformNode?: unknown;
    _babylonTransformNodeForSkin?: unknown;
    _primitiveBabylonMeshes?: unknown[];
}

class TraceAnimationBindingNames implements IGLTFLoaderExtension {
    readonly name = TRACE_ANIMATION_BINDING;
    enabled = true;
    private _loader: GLTFLoader | null;

    constructor(loader: GLTFLoader) {
        this._loader = loader;
    }

    onReady(): void {
        const gltf = this._loader?.gltf;
        if (gltf) stampDrivers(gltf);
        if (!gltf?.nodes?.length) return;
        const names = resolveFileNames(gltf);
        (gltf.nodes as unknown as LoadedGltfNode[]).forEach((node, index) => {
            stamp(node._babylonTransformNode, names);
            stamp(node._babylonTransformNodeForSkin, names);
            for (const mesh of node._primitiveBabylonMeshes ?? []) stamp(mesh, names, index);
        });
    }

    dispose(): void {
        this._loader = null;
    }
}

unregisterGLTFExtension(TRACE_ANIMATION_BINDING);
registerGLTFExtension(TRACE_ANIMATION_BINDING, false, (loader) => new TraceAnimationBindingNames(loader));
